/**
 * Smooth scroll utility for navigation
 */

export interface ScrollOptions {
  offset?: number;
  duration?: number;
  easing?: 'linear' | 'easeInOut' | 'easeOut';
}

/**
 * Easing functions
 */
const easingFunctions = {
  linear: (t: number) => t,
  easeInOut: (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
  easeOut: (t: number) => t * (2 - t)
};

/**
 * Smooth scroll to element
 */
export function smoothScrollTo(element: Element | string, options: ScrollOptions = {}): Promise<void> {
  return new Promise((resolve) => {
    const {
      offset = 0,
      duration = 800,
      easing = 'easeInOut'
    } = options;

    let targetElement: Element | null = null;

    if (typeof element === 'string') {
      // First try regular document query
      targetElement = document.querySelector(element);

      // If not found, search in Shadow DOM of bnb-app
      if (!targetElement) {
        const bnbApp = document.querySelector('bnb-app');
        if (bnbApp?.shadowRoot) {
          targetElement = bnbApp.shadowRoot.querySelector(element);
        }
      }
    } else {
      targetElement = element;
    }

    if (!targetElement) {
      console.warn(`Element not found: ${element}`);
      resolve();
      return;
    }

    const startPosition = window.pageYOffset;
    const targetPosition = targetElement.getBoundingClientRect().top + startPosition - offset;
    const distance = targetPosition - startPosition;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      // Instant scroll for reduced motion
      window.scrollTo(0, targetPosition);
      resolve();
      return;
    }

    const startTime = performance.now();
    const easingFunction = easingFunctions[easing];

    function scrollStep(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = easingFunction(progress);

      window.scrollTo(0, startPosition + distance * ease);

      if (progress < 1) {
        requestAnimationFrame(scrollStep);
      } else {
        resolve();
      }
    }

    requestAnimationFrame(scrollStep);
  });
}

/**
 * Smooth scroll to top
 */
export function smoothScrollToTop(options: ScrollOptions = {}): Promise<void> {
  return new Promise((resolve) => {
    const {
      duration = 800,
      easing = 'easeInOut'
    } = options;

    const startPosition = window.pageYOffset;
    const distance = -startPosition;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      // Instant scroll for reduced motion
      window.scrollTo(0, 0);
      resolve();
      return;
    }

    const startTime = performance.now();
    const easingFunction = easingFunctions[easing];

    function scrollStep(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = easingFunction(progress);

      window.scrollTo(0, startPosition + distance * ease);

      if (progress < 1) {
        requestAnimationFrame(scrollStep);
      } else {
        resolve();
      }
    }

    requestAnimationFrame(scrollStep);
  });
}

/**
 * Initialize smooth scroll for all anchor links
 */
export function initSmoothScroll(options: ScrollOptions = {}) {
  document.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a[href^="#"]') as HTMLAnchorElement;

    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') return;

    e.preventDefault();
    smoothScrollTo(href, options);
  });
}

/**
 * Check if element is in viewport
 */
export function isInViewport(element: Element, threshold: number = 0): boolean {
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  return (
    rect.top <= windowHeight - threshold &&
    rect.bottom >= threshold &&
    rect.left <= windowWidth - threshold &&
    rect.right >= threshold
  );
}

/**
 * Observe elements for scroll animations
 */
export function observeElements(
  selector: string,
  callback: (element: Element, isIntersecting: boolean) => void,
  options?: IntersectionObserverInit
): IntersectionObserver {
  const elements = document.querySelectorAll(selector);

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      callback(entry.target, entry.isIntersecting);
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px',
    ...options
  });

  elements.forEach(element => observer.observe(element));

  return observer;
}